import { useCallback, useEffect, useRef, useState } from "react";

const TTS_URL = process.env.REACT_APP_TTS_URL || "/api/elevenlabs-tts.php";
const PING_URL = process.env.REACT_APP_PING_URL || "/api/ping.php";

/**
 * ElevenLabs TTS hook
 * - Keeps your existing endpoint logic
 * - Adds player controls + replay + progress + boundary callbacks
 * - Caches the latest audio in-memory (object URL) so Replay is instant
 */
export function useElevenLabsTTS({ onSpeakChange, onBoundary }) {
  const audioRef = useRef(null);
  const lastUrlRef = useRef(null);
  const lastTextRef = useRef("");

  const [debug, setDebug] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blocked, setBlocked] = useState(false);

  const cleanupUrl = useCallback(() => {
    if (lastUrlRef.current) {
      try {
        URL.revokeObjectURL(lastUrlRef.current);
      } catch {}
      lastUrlRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try {
        a.pause();
        a.currentTime = 0;
      } catch {}
    }
    setIsPlaying(false);
    setProgress(0);
    onSpeakChange?.(false);
  }, [onSpeakChange]);

  const pause = useCallback(() => {
    const a = audioRef.current;
    if (a) {
      try {
        a.pause();
      } catch {}
    }
    setIsPlaying(false);
    onSpeakChange?.(false);
  }, [onSpeakChange]);

  const resume = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      setBlocked(false);
      await a.play();
      setIsPlaying(true);
      onSpeakChange?.(true);
    } catch (e) {
      setBlocked(true);
      setIsPlaying(false);
      onSpeakChange?.(false);
      setDebug("Audio blocked (autoplay). Klik op Play.");
      console.warn("[TTS] audio.play blocked:", e);
    }
  }, [onSpeakChange]);

  const ping = useCallback(async () => {
    try {
      setDebug(`Pinging: ${PING_URL}`);
      const res = await fetch(PING_URL, { method: "GET" });
      const txt = await res.text();
      setDebug(`Ping status ${res.status}: ${txt}`);
      console.log("[TTS] ping response:", res.status, txt);
      return res.ok;
    } catch (e) {
      setDebug(`Ping failed: ${String(e)}`);
      console.error("[TTS] ping failed:", e);
      return false;
    }
  }, []);

  const attachAudioEvents = useCallback(
    (audio, text) => {
      audio.ontimeupdate = () => {
        if (!audio.duration) return;
        const p = audio.currentTime / audio.duration;
        const clamped = Number.isFinite(p) ? Math.max(0, Math.min(1, p)) : 0;
        setProgress(clamped);
        const charIndex = Math.floor(clamped * text.length);
        onBoundary?.(charIndex);
      };

      audio.onplay = () => {
        setIsPlaying(true);
        onSpeakChange?.(true);
      };

      audio.onpause = () => {
        setIsPlaying(false);
        onSpeakChange?.(false);
      };

      audio.onended = () => {
        setIsPlaying(false);
        setProgress(1);
        onSpeakChange?.(false);
        setDebug("Audio ended ✅");
      };

      audio.onerror = (e) => {
        setIsPlaying(false);
        setProgress(0);
        onSpeakChange?.(false);
        setDebug("Audio playback error ❌");
        console.error("[TTS] audio error:", e);
      };
    },
    [onBoundary, onSpeakChange]
  );

  const speak = useCallback(
    async (text) => {
      const t = (text || "").trim();
      if (!t) return;

      setBlocked(false);
      lastTextRef.current = t;

      stop();

      setDebug(`POST ${TTS_URL} (${t.length} chars)`);
      console.log("[TTS] calling:", TTS_URL, "len:", t.length);

      try {
        const res = await fetch(TTS_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: t }),
        });

        const contentType = res.headers.get("content-type") || "";
        console.log("[TTS] status:", res.status, "content-type:", contentType);

        if (!res.ok) {
          const errText = await res.text();
          setDebug(`TTS error ${res.status}: ${errText.slice(0, 180)}`);
          console.error("[TTS] error body:", errText);
          onSpeakChange?.(false);
          return;
        }

        if (!contentType.includes("audio")) {
          const txt = await res.text();
          setDebug(`Unexpected content-type: ${contentType}. Body: ${txt.slice(0, 220)}`);
          console.warn("[TTS] unexpected response:", txt);
          onSpeakChange?.(false);
          return;
        }

        const blob = await res.blob();
        setDebug(`Received audio blob: ${Math.round(blob.size / 1024)} KB`);

        cleanupUrl();
        const audioUrl = URL.createObjectURL(blob);
        lastUrlRef.current = audioUrl;

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        setProgress(0);
        onBoundary?.(0);

        attachAudioEvents(audio, t);

        try {
          await audio.play();
          setDebug("Audio playing ▶️");
        } catch (e) {
          setBlocked(true);
          setIsPlaying(false);
          onSpeakChange?.(false);
          setDebug("Audio blocked (autoplay). Klik op Play.");
          console.warn("[TTS] audio.play blocked:", e);
        }
      } catch (e) {
        setDebug(`Fetch failed: ${String(e)}`);
        console.error("[TTS] fetch failed:", e);
        onSpeakChange?.(false);
      }
    },
    [attachAudioEvents, cleanupUrl, onBoundary, onSpeakChange, stop]
  );

  const replay = useCallback(async () => {
    const a = audioRef.current;
    if (a) {
      try {
        a.currentTime = 0;
      } catch {}
      await resume();
      return;
    }

    if (!lastUrlRef.current) return;
    const audio = new Audio(lastUrlRef.current);
    audioRef.current = audio;
    attachAudioEvents(audio, lastTextRef.current || "");
    try {
      await audio.play();
    } catch (e) {
      setBlocked(true);
      setDebug("Audio blocked (autoplay). Klik op Play.");
      console.warn("[TTS] replay blocked:", e);
    }
  }, [attachAudioEvents, resume]);

  useEffect(() => {
    return () => {
      try {
        audioRef.current?.pause?.();
      } catch {}
      cleanupUrl();
    };
  }, [cleanupUrl]);

  return {
    speak,
    replay,
    stop,
    pause,
    resume,

    isPlaying,
    progress,
    hasLastAudio: !!lastUrlRef.current || !!lastTextRef.current,

    ping,
    debug,
    blocked,
    ttsUrl: TTS_URL,
  };
}
